import { DataProxy } from 'apollo-cache'
import {
  GetSharesDocument,
  GetSharesQuery,
  GetSharesQueryVariables,
  GetProjectSharesDocument,
  GetProjectSharesQuery,
  GetProjectSharesQueryVariables,
  GetInitialUserQuery,
  GetInitialUserDocument,
  GetInitialUserQueryVariables,
} from '@sketch/gql-types'

// Here FilterTypes are used in non exhaustive way
// therefore build should not break after SketchQL deployments
// eslint-disable-next-line no-restricted-imports
import { WorkspaceShareFilterType } from '@sketch/gql-types/expansive'
import produce from 'immer'
import { ErrorHandler } from '@sketch/tracing'
import { castError } from '@sketch/utils'
import { errorPreventiveCacheRead } from '@sketch/modules-common'

export type SharesPagination = NonNullable<
  GetSharesQuery['workspace']['shares']
>

interface UpdateSharesCache {
  cache: DataProxy
  update: (data: SharesPagination) => void
  workspaceId: string
  filter?: WorkspaceShareFilterType | null
}

export const updateSharesCache = ({
  cache,
  update,
  workspaceId,
  filter = null,
}: UpdateSharesCache) => {
  const variables = {
    identifier: workspaceId,
    after: null,
    search: {
      name: '',
      filters: filter ? [filter] : undefined,
    },
  }
  let existingData: GetSharesQuery | null
  try {
    existingData = cache.readQuery<GetSharesQuery, GetSharesQueryVariables>({
      query: GetSharesDocument,
      variables,
    })
  } catch (e) {
    const err = castError(e)
    ErrorHandler.ignore(err)
    existingData = null
  }

  if (!existingData) return

  const updated = produce(existingData, draft => {
    update(draft.workspace.shares as SharesPagination)
  })

  cache.writeQuery<GetSharesQuery, GetSharesQueryVariables>({
    query: GetSharesDocument,
    data: updated,
    variables,
  })
}

interface UpdateProjectSharesCache {
  cache: DataProxy
  update: (data: SharesPagination) => void
  projectShortId: string
}

export const updateProjectSharesCache = ({
  cache,
  update,
  projectShortId,
}: UpdateProjectSharesCache) => {
  // Read user profile from the cache to check "collections" FF
  const userProfile = errorPreventiveCacheRead<
    GetInitialUserQuery,
    GetInitialUserQueryVariables
  >(cache, {
    query: GetInitialUserDocument,
  })

  // TODO: Remove collections FF
  // https://github.com/sketch-hq/Cloud/issues/15999
  const isCollectionFFEnabled = userProfile?.me.featureFlags?.includes(
    'collections'
  )

  const variables = {
    shortId: projectShortId,
    after: null,
    search: {
      name: null,
      isCurrentVersionDownloadable: null,
      filters: isCollectionFFEnabled ? ['NO_COLLECTION' as const] : undefined,
    },
  }

  let existingData: GetProjectSharesQuery | null
  try {
    existingData = cache.readQuery<
      GetProjectSharesQuery,
      GetProjectSharesQueryVariables
    >({
      query: GetProjectSharesDocument,
      variables,
    })
  } catch (e) {
    const err = castError(e)
    ErrorHandler.ignore(err)
    existingData = null
  }

  if (!existingData) return

  const updated = produce(existingData, draft => {
    update(draft.project.shares as SharesPagination)
  })

  cache.writeQuery<GetProjectSharesQuery, GetProjectSharesQueryVariables>({
    query: GetProjectSharesDocument,
    data: updated,
    variables,
  })
}
